import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { authFetch } from "@/api/auth-fetch";
import { useAuthStore } from "@/stores/auth";
import type { Brand, Language } from "@/api/models";

/**
 * Admin-side CRUD for the `brands` table that anchors the multi-brand
 * model (Web1, Mail2, Net3, …). The list endpoint returns every brand;
 * the detail endpoints power create + update from the BrandsView.
 */
export interface CreateBrandInput {
  code: string;
  name: string;
  legalName?: string | null;
  businessId?: string | null;
  vatId?: string | null;
  domain?: string | null;
  defaultEmailFrom?: string | null;
  defaultEmailFromName?: string | null;
  /** Palvelutiedotteiden (NOTICE) lähettäjä. Erillinen
   *  `defaultEmailFrom`ista, koska sama kenttä ohjaisi myös
   *  transaktionaaliset viestit (salasanan palautus,
   *  allekirjoituslinkki). NULL = ei ohitusta. */
  noticeEmailFrom?: string | null;
  /** Per-brand Postal HTTP API relay (Hattara). Leave both null to
   *  fall back to the global POSTAL_API_URL/_KEY env vars. */
  postalApiUrl?: string | null;
  postalApiKey?: string | null;
  /** Web1 Billing integration credentials. sender_id is the UUID
   *  assigned by Web1 Billing when the sender is created in its admin
   *  UI; api_key + notification_secret are write-only and never
   *  echoed back. Pass `null` to clear a previously-set value. */
  billingSenderId?: string | null;
  billingApiKey?: string | null;
  billingNotificationSecret?: string | null;
  /** Yhteystunnusten brändiprefiksi (esim. "N3" → N3-100001), 2–4
   *  merkkiä A-Z0-9. Backend normalisoi isoksi. Null = brändin
   *  sopimusriveille ei generoida yhteystunnuksia. */
  connectionIdPrefix?: string | null;
  defaultLanguage?: Language;
  defaultCurrency?: string;
  isActive?: boolean;
  sortOrder?: number;
}

export type UpdateBrandInput = Partial<Omit<CreateBrandInput, "code">>;

async function unwrapData<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as
      | { message?: string | string[] }
      | null;
    throw new Error(
      Array.isArray(err?.message)
        ? err?.message.join(", ")
        : (err?.message ?? `HTTP ${res.status}`),
    );
  }
  const body = (await res.json()) as { data: T };
  return body.data;
}

export const useBrandsStore = defineStore("brands", () => {
  const list = ref<Brand[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const activeBrands = computed(() => list.value.filter((b) => b.isActive));

  async function fetchList(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const res = await authFetch("/api/v1/brands");
      list.value = await unwrapData<Brand[]>(res);
    } catch (e) {
      error.value = (e as Error).message;
      list.value = [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Hae brand-lista AINA admin-scope:lla, ohittaen X-Brand-Code-
   * headerin. Backendin `brands.list(scope)` suodattaa brand-bound-
   * callereille vain heidän brandinsa — jos brand-switcher käyttäisi
   * tavallista `fetchList`:iä WEB1-tilassa, näkisi vain yhden
   * vaihtoehdon (alkuperäinen bug 2026-05-22).
   *
   * Käytetään raw `fetch`-kutsua jotta `authFetch`:n implisiittinen
   * brand-konteksti ei pääse väliin — vain Bearer-tunnistus.
   * BrandAccessGuard sallii admin-scopen kaikille tunnistautuneille
   * (vain kirjoitusoperaatiot vaativat allowlist:n). UserBrand-
   * suodatus tehdään GUI-puolella BrandSwitcher:in options:ssa.
   *
   * Talletetaan erilliseen `allBrands`-refiin jotta normaali `list`
   * (esim. brand-admin-CRUD-näkymässä) voi edelleen olla brand-scoped.
   */
  const allBrands = ref<Brand[]>([]);
  async function fetchAllUnscoped(): Promise<void> {
    const auth = useAuthStore();
    if (!auth.tokens?.accessToken) return;
    try {
      const res = await fetch("/api/v1/brands", {
        headers: { Authorization: `Bearer ${auth.tokens.accessToken}` },
      });
      if (!res.ok) {
        throw new Error(`Failed to load brand list (${res.status})`);
      }
      allBrands.value = await unwrapData<Brand[]>(res);
    } catch (e) {
      error.value = (e as Error).message;
      allBrands.value = [];
    }
  }
  const allActiveBrands = computed(() =>
    allBrands.value.filter((b) => b.isActive),
  );

  async function getById(id: string): Promise<Brand> {
    const res = await authFetch(`/api/v1/brands/${id}`);
    return unwrapData<Brand>(res);
  }

  async function create(input: CreateBrandInput): Promise<Brand> {
    const res = await authFetch("/api/v1/brands", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const created = await unwrapData<Brand>(res);
    await fetchList();
    return created;
  }

  /// Operator pings Web1 Billing with the brand's stored creds.
  /// Returns the raw result shape so the editor can render
  /// `ok=true` as a green check + duration, `ok=false` as a red
  /// pill with the upstream error message.
  async function testBilling(id: string): Promise<{
    ok: boolean;
    status: number;
    durationMs: number;
    message: string;
  }> {
    const res = await authFetch(
      `/api/v1/brands/${encodeURIComponent(id)}/billing-test`,
      { method: "POST" },
    );
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { message?: string }
        | null;
      throw new Error(body?.message ?? `HTTP ${res.status}`);
    }
    const body = (await res.json()) as {
      data: {
        ok: boolean;
        status: number;
        durationMs: number;
        message: string;
      };
    };
    return body.data;
  }

  async function update(id: string, input: UpdateBrandInput): Promise<Brand> {
    const res = await authFetch(`/api/v1/brands/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const updated = await unwrapData<Brand>(res);
    await fetchList();
    return updated;
  }

  return {
    list,
    activeBrands,
    allBrands,
    allActiveBrands,
    loading,
    error,
    fetchList,
    fetchAllUnscoped,
    getById,
    create,
    update,
    testBilling,
  };
});
